import React from "react";
import { Box, Card, styled, Grid, Typography } from "@mui/material";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { featuresCard } from "./FeaturesCard";
import { addFeature } from "../blog/BlogSlice";
import { LazyLoadImage } from "react-lazy-load-image-component";
import FeatureDemoThumb from "../../../components/FeatureDemoThumb";
import addCircleWhite from "../../../assets/new_design/add_circle_white.png";

const SectionWrapper = styled(Box)({
  backgroundColor: "#F6F7F9",
  padding: "60px 0",
});

const FeatureCard = styled(Card)(({ theme }) => ({
  width: "100%",
  height: "auto",
  borderRadius: "42.62px",
  backgroundColor: "#FFFFFF",
  boxShadow: "none",
  display: "flex",
  flexDirection: "column",
  padding: "32px",
  transition: "transform 0.3s ease-in-out",
  cursor: "pointer",
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: "none",
  },
  [theme.breakpoints.down("md")]: {
    padding: "24px",
    borderRadius: "24px",
  },
}));

const CardTitle = styled(Typography)({
  fontFamily: "'Red Hat Display'",
  fontWeight: 600,
  fontSize: "20px",
  color: "#0F172A",
  lineHeight: "1.2",
  marginBottom: "16px",
});

const CardDescription = styled(Typography)({
  fontFamily: "'Red Hat Display'",
  fontWeight: 400,
  fontSize: "18px",
  color: "#475569",
  lineHeight: "1.6",
  marginBottom: "8px",
});

const MoreDetailsButton = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  backgroundColor: "#2563EB",
  color: "#FFFFFF",
  padding: "10px 20px",
  borderRadius: "12px",
  width: "fit-content",
  cursor: "pointer",
  marginTop: "auto",
  textDecoration: "none",
  "&:hover": {
    backgroundColor: "#1D4ED8",
  },
});

function OtherFeaturesSection({ currentId }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const otherFeatures = featuresCard.filter((feature) => feature.id !== currentId);

  const handleCardClick = (item) => {
    localStorage.setItem("selectedFeature", JSON.stringify(item));
    dispatch(addFeature(item));
    if (item.link) {
      navigate(item.link);
    } else if (item.id === "connectedpapers") {
      navigate(`/connectedpapers`);
    } else {
      navigate(`/features/${item.id}`);
    }
  };

  return (
    <SectionWrapper>
      <Box sx={{ maxWidth: "1600px", margin: "0 auto", px: { xs: "24px", md: "40px" } }}>
        <Box sx={{ textAlign: "center", mb: { xs: "32px", md: "48px" } }}>
          <Typography
            component="h2"
            sx={{
              fontFamily: "'Red Hat Display'",
              fontWeight: 700,
              fontSize: { xs: "28px", md: "36px" },
              color: "#0F172A",
              lineHeight: "1.2",
              mb: 1.5,
            }}
          >
            Explore More Features
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Red Hat Display'",
              fontWeight: 400,
              fontSize: { xs: "16px", md: "18px" },
              color: "#475569",
              maxWidth: "800px",
              margin: "0 auto",
              lineHeight: "1.5",
            }}
          >
            Discover the rest of ResearchPal's AI-powered research and writing tools.
          </Typography>
        </Box>

        <Grid container spacing={4} justifyContent="center">
          {otherFeatures.map((feature) => (
            <Grid item xs={12} sm={6} md={4} key={feature.id} sx={{ display: "flex" }}>
              <FeatureCard onClick={() => handleCardClick(feature)}>
                <Box sx={{ mb: 4, width: "100%", height: { xs: "180px", md: "200px" }, overflow: "hidden", borderRadius: "8px" }}>
                  {feature.demo ? (
                    <FeatureDemoThumb
                      demo={feature.demo}
                      naturalHeight={feature.demoHeight}
                      demoProps={feature.demoProps}
                    />
                  ) : (
                    <LazyLoadImage
                      src={feature.img}
                      alt={feature.title}
                      effect="blur"
                      width="100%"
                      height="100%"
                      style={{ objectFit: "cover" }}
                    />
                  )}
                </Box>
                <CardTitle>{feature.title}</CardTitle>
                <CardDescription>{feature.description}</CardDescription>
                <MoreDetailsButton
                  component={Link}
                  to={feature.link || (feature.id === "connectedpapers" ? "/connectedpapers" : `/features/${feature.id}`)}
                  onClick={(e) => e.stopPropagation()}
                >
                  <img src={addCircleWhite} alt="add" style={{ width: "20px", height: "20px" }} />
                  <Typography sx={{ fontFamily: "'Red Hat Display'", fontWeight: 500, fontSize: "14px" }}>
                    {feature.buttonText || "More details"}
                  </Typography>
                </MoreDetailsButton>
              </FeatureCard>
            </Grid>
          ))}
        </Grid>
      </Box>
    </SectionWrapper>
  );
}

export default OtherFeaturesSection;
